import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CategoryDetailsData,
  HistoryState,
  TableMetaData,
} from '../PaymentIntegrityPage/PaymentIntegrity.types';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  BasicTable,
  Breadcrumbs,
  CardComponent,
  DashedStandardDropdown,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { paymentIntegrityConstants } from '../../core-utils/messages/paymentIntegrityConstants';
import Snackbar from '../../_components/atoms/Snackbar';
import Loader from '../../_components/molecules/Loader';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import {
  formatCategoryDetails,
  getPaymentIntegrityCategoryDetailsColumns,
} from '../../core-utils/helper-utils/paymentIntegrityHelper';
import { getCategoryDetails } from '../../core-utils/axios/payment-integrity/paymentIntegrity';
import { useStores } from '../../stores';
import { observer } from 'mobx-react';
import { PAYMENT_INTEGRITY } from '../../core-utils/routes';
import {
  getDateSelection,
  getSubTextTypography,
} from '../PaymentIntegrityPage';
import { PlanDates } from '../../core-utils/custom-hooks/PlanDates';
import { getPlanDate } from '../../_components/molecules/QuaterlyReportModal';

const PaymentIntegrityInternalPage = observer(() => {
  const { classes } = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '1182px',
      padding: '40px 0px 66px 63px',
    },
    hr: {
      width: '100%',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      margin: '18px 0px',
    },
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    overviewCardContainer: {
      display: 'flex',
      gap: '1rem',
    },
    flex: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
  })();

  const messages = paymentIntegrityConstants;
  const pageSize = 10;

  const navigate = useNavigate();
  const { state } = useLocation() as { state: HistoryState };
  const { snackbarState, handleCloseSnackBar, openSnackbar } = useSnackbar();
  const employerGroupStore = useStores().employerGroupStore;
  const quaterlyReportStore = useStores().quaterlyReportStore;
  const egNid = employerGroupStore?.employerGroupResponseData?.eg_nid[0];
  const dropDownOptionsPlanPeriod: string[] = quaterlyReportStore.planDates.map(
    (plan) => getPlanDate(plan),
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [categoryDetailsData, setCategoryDetailsData] = useState<
    CategoryDetailsData[]
  >([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);
  const [tableMetaData, setTableMetaData] = useState<TableMetaData>({
    category_description: '',
    column_header: '',
  });
  const [categoryDropdownValue, setCategoryDropdownValue] = useState<string>(
    state.category,
  );

  const currentPlan: PlanDates = useMemo(() => {
    return quaterlyReportStore.planDates?.[
      quaterlyReportStore.selectedPlanIndex
    ];
  }, [quaterlyReportStore.planDates, quaterlyReportStore.selectedPlanIndex]);

  const planPeriod = useMemo(() => {
    return currentPlan ? getPlanDate(currentPlan) : '';
  }, [currentPlan]);

  const breadcrumbItemList = [
    [
      'text',
      'Payment Integrity',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => navigate(PAYMENT_INTEGRITY),
    ],
    [
      'text',
      categoryDropdownValue,
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];

  const handlePagination = (event: ChangeEvent<unknown>, page: number) => {
    setPageNumber(page);
  };
  const handlePlanPeriodChange = (event: ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    const planIndex = dropDownOptionsPlanPeriod.findIndex(
      (plan) => plan === value,
    );

    quaterlyReportStore.setSelectedPlanIndex(planIndex);
  };

  const TableCard = () => {
    return (
      <CardComponent
        title={categoryDropdownValue}
        titleSubTitleMargin="1rem"
        body={
          <BasicTable
            columns={getPaymentIntegrityCategoryDetailsColumns(
              tableMetaData.column_header,
              categoryDropdownValue,
            )}
            rowsData={formatCategoryDetails(
              categoryDetailsData,
              categoryDropdownValue,
            )}
            displayType="row"
            isFullTable
            headerCellHeight="46px"
            headerColor={patientAccessTheme.palette.grey[700]}
            headerTextColor={patientAccessTheme.palette.status.white}
            cellHeight="46px"
            cellPadding="12px 9px 12px 14px"
            isCollapsiblePatientTable={true}
            showPagination={true}
            handlePagination={handlePagination}
            pageNumber={pageNumber}
            pageCount={pageCount}
            isToolTipFirstColumn={true}
          />
        }
        tooltip={tableMetaData.category_description}
      />
    );
  };

  const getCategoryDetailsAPI = async () => {
    setLoading(true);
    try {
      const response = await getCategoryDetails(
        egNid,
        categoryDropdownValue,
        currentPlan.paid_start_date,
        currentPlan.paid_end_date,
        pageNumber - 1,
      );

      const category = response.data.data;
      const updatedData = category?.updatedCategories ?? [];

      if (!updatedData.length) {
        setCategoryDetailsData([]);
        setPageCount(0);
        setTableMetaData({
          category_description: '',
          column_header: '',
        });
        return;
      }

      setCategoryDetailsData(updatedData);
      setPageCount(Math.ceil(category?.totalCount / pageSize));
      setTableMetaData({
        category_description: updatedData[0].category_description,
        column_header: updatedData[0].column_header,
      });
    } catch (error) {
      openSnackbar(messages.ERROR_CATEGORY, 'error', 2000);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!egNid || !currentPlan) {
      return;
    }
    getCategoryDetailsAPI();
  }, [egNid, currentPlan, pageNumber, categoryDropdownValue]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Grid className={classes.root}>
          <Grid style={{ marginBottom: '1rem' }}>
            <Breadcrumbs itemList={breadcrumbItemList} />
          </Grid>

          <PageTitleAndSubTitle
            title={
              <Grid className={classes.flex}>
                <DashedStandardDropdown
                  options={state.categoryList}
                  displayValue={state.categoryList}
                  value={categoryDropdownValue}
                  handleChange={(event: any) => {
                    const value: string = event.target.value as string;
                    setCategoryDropdownValue(value);
                    setPageNumber(1);
                  }}
                />
                {getSubTextTypography('for')}
                {getDateSelection(
                  planPeriod,
                  dropDownOptionsPlanPeriod,
                  handlePlanPeriodChange,
                  classes.flex,
                )}
              </Grid>
            }
            subTitle={messages.SUBTITLE}
          />
          <hr className={classes.hr} />
          <Grid className={classes.mainContainer}>
            <TableCard />
          </Grid>
        </Grid>
      )}
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </>
  );
});

export default PaymentIntegrityInternalPage;
